import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Pagination, Tips } from '@/components/common'
import { Modal } from '@/components/common/Modal'
import {
    BasicInfoForm,
    DemographicForm,
    EducationForm,
    JobInformationForm,
    AboutYouForm
} from '@/components/forms'
import { AcademicAdmissionForm } from '@/components/forms/AcademicAdmission/AcademicAdmission'
import {
    JobAuthorization,
    StudyAuthorization
} from '@/components/forms/Authorization'
import { useDisclosure } from '@/components/nextui'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useAuthStore } from '@/store/authStore'
import { MessageLevel } from '@/types'
import { ErrorHandlingStrategy } from '@/types/api'
import { FormsProps } from '@/types/forms'

const MEET_US_TOTAL_PAGES = 8
/** Página por defecto si `idLastForm` del backend es inválido o la vista quedaría en blanco */
const MEET_US_FALLBACK_PAGE = 5

function isValidMeetUsPage(p: number): boolean {
    return Number.isInteger(p) && p >= 1 && p <= MEET_US_TOTAL_PAGES
}

function normalizeMeetUsPage(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value)

    if (
        !Number.isFinite(n) ||
        !Number.isInteger(n) ||
        n < 1 ||
        n > MEET_US_TOTAL_PAGES
    ) {
        return MEET_US_FALLBACK_PAGE
    }

    return n
}

// eslint-disable-next-line max-lines-per-function
export const MeetUsModule = ({
    onNext,
    onPrevious,
    idLastForm,
    completed,
    progress,
    updateForms,
    initialPage
}: FormsProps): JSX.Element => {
    const { t } = useTranslation()
    const { isOpen, onClose, onOpen } = useDisclosure()
    const { idInscription, inscriptions } = useAuthStore()
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [shouldBlock, setShouldBlock] = useState<boolean>(false)
    const [textModal, setTextModal] = useState<string>('inscriptionDataChange')

    const currentInscription = inscriptions?.find((i) => i.id === idInscription)
    const isInactiveInscription =
        currentInscription && !currentInscription.isActive

    /** Varias inscripciones + la actual activa: sin paginador hasta completar el módulo; siempre empezar en página 1 */
    const restrictMeetUsPagination = useMemo(
        () =>
            (inscriptions?.length ?? 0) > 1 &&
            Boolean(currentInscription?.isActive),
        [inscriptions, currentInscription]
    )
    // Página pedida desde otro módulo (p. ej. link a Educación desde documentos)
    const hasInitialPage =
        initialPage !== undefined && isValidMeetUsPage(initialPage)
    const [page, setPage] = useState<number>(() =>
        hasInitialPage ? initialPage : normalizeMeetUsPage(idLastForm)
    )
    const hasSyncedIdLastFormRef = useRef(hasInitialPage)

    useEffect(() => {
        hasSyncedIdLastFormRef.current = hasInitialPage
    }, [idInscription])

    useLayoutEffect(() => {
        if (restrictMeetUsPagination && !completed && !hasInitialPage) {
            setPage(1)
        }
    }, [restrictMeetUsPagination, completed, idInscription])

    // Cuando el backend envía un idLastForm válido (p. ej. tras cargar steps), alinear la página una vez
    useEffect(() => {
        if (restrictMeetUsPagination && !completed) {
            return
        }

        const raw = Number(idLastForm)

        if (hasSyncedIdLastFormRef.current) {
            return
        }

        if (isValidMeetUsPage(raw)) {
            setPage(raw)
            hasSyncedIdLastFormRef.current = true
        }
    }, [idLastForm, restrictMeetUsPagination, completed])

    // Si el estado queda fuera de 1–8 (datos raros), volver a página segura
    useEffect(() => {
        if (!isValidMeetUsPage(page)) {
            if (restrictMeetUsPagination && !completed) {
                setPage(1)
            } else {
                setPage(normalizeMeetUsPage(idLastForm))
            }
        }
    }, [page, idLastForm, restrictMeetUsPagination, completed])

    // Verificar si el aspirante está admitido/matriculado (inscriptionStatusId === 3, 4 o 6)
    const isAdmitted = (inscriptionStatusId: number): boolean => {
        return (
            inscriptionStatusId === 3 ||
            inscriptionStatusId === 4 ||
            inscriptionStatusId === 6
        )
    }

    useEffect(() => {
        const fetchInscriptionStatus = async () => {
            if (!idInscription) {
                return
            }

            if (isInactiveInscription) {
                setShouldBlock(true)
                setTextModal('inscriptionInactive')

                return
            }

            const { data, status } = await inscriptionsApi.getInscription(
                String(idInscription)
            )

            if (status === 200 && data) {
                setShouldBlock(isAdmitted(data.inscriptionStatusId))
            }
        }

        fetchInscriptionStatus()
    }, [idInscription, isInactiveInscription])

    // Efecto para mostrar el modal automáticamente cuando se cumplen las condiciones
    useEffect(() => {
        if (shouldBlock) {
            onOpen()
        }
    }, [shouldBlock, onOpen])

    // Función para manejar clicks en campos bloqueados
    const handleBlockedFieldClick = (): void => {
        if (shouldBlock) {
            onOpen()
        }
    }

    const onNextPage = (): void => {
        // Datos básicos puede ser lo único pendiente (p. ej. autorización de
        // menor tras cambiar a TI): refrescar el estado del módulo al guardarlo
        if (page === 2 && !completed) {
            updateForms?.()
        }

        if (page !== 8) {
            setPage((page) => page + 1)
            scroll(0, 100)
        } else {
            onNext()
        }
    }
    const previousPage = (): void => {
        if (page !== 1) {
            setPage((page) => page - 1)
        } else {
            onPrevious()
        }
    }

    const effectivePage = isValidMeetUsPage(page)
        ? page
        : normalizeMeetUsPage(idLastForm)
    const hasInvalidLastFormId =
        idLastForm !== undefined &&
        idLastForm !== null &&
        !isValidMeetUsPage(Number(idLastForm))

    const renderPage = (): JSX.Element | undefined => {
        switch (effectivePage) {
            case 1:
                return (
                    <AcademicAdmissionForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 2:
                return (
                    <BasicInfoForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 3:
                return (
                    <DemographicForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 4:
                return (
                    <EducationForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 5:
                return (
                    <JobInformationForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 6:
                return (
                    <AboutYouForm
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 7:
                return (
                    <JobAuthorization
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )
            case 8:
                return (
                    <StudyAuthorization
                        onNext={onNextPage}
                        onPrevious={previousPage}
                        isReadOnly={shouldBlock}
                        onBlockedFieldClick={handleBlockedFieldClick}
                    />
                )

            default:
                break
        }
    }
    const [tips, setTips] = useState(0)

    useEffect(() => {
        const interval = setInterval(() => {
            setTips((tip) => {
                if (tip >= 32) {
                    return (tip = 0)
                } else {
                    return tip + 1
                }
            })
        }, 10000)

        return (): void => clearInterval(interval)
    }, [])

    return (
        <section className="space-y-2">
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                text={textModal}
            />
            {effectivePage !== 4 && effectivePage !== 6 && (
                <Tips
                    text={t(`tips.random.${tips}`)}
                    title={t('tips.knowIt')}
                    banner="news"
                    className="mt-8"
                />
            )}
            {renderPage()}
            <Pagination
                currentPage={effectivePage}
                totalPages={MEET_US_TOTAL_PAGES}
                completed={completed}
                alwaysVisible={hasInvalidLastFormId}
                onChange={(page) => {
                    setPage(page)
                }}
            />
        </section>
    )
}
